import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import type {
  BehandelaarSummary,
  UserProfile,
  UpdateUserProfileRequest,
  WorkingHours,
  UpdateWorkingHoursRequest,
  AvailabilityException,
  AvailabilityExceptionRequest
} from '@/api/generated-users'
import { initApiService } from '@/services/init'
import { getGlobalT } from '@/i18n'
import { extractApiErrorMessage, logUnexpectedFailure } from '@/utils/errorHandling'
import { handleSessionExpired, isSessionExpiredError, refreshIdToken } from '@/services/sessionExpiry'
import { clearSentryUserContext, setSentryUserContext } from '@/utils/sentryUserContext'
import { getFullName, sortByFullName } from '@/utils/userFormatting'
import { isAxiosError } from 'axios'
import type { PermissionValue } from '@/utils/permissionBitmask'

/**
 * A concrete agenda time range, stored under `preferences.agenda`.
 * Times are `HH:mm` strings. `endTime` may be `24:00` to mean end-of-day.
 * The concrete range (not an enum) is persisted so future custom ranges
 * need no schema/API change (#2893).
 */
export interface AgendaTimeRange {
  startTime: string
  endTime: string
}

/** Default range: opens the agenda on the working day, not the night. */
export const WERKDAG_RANGE: AgendaTimeRange = { startTime: '07:00', endTime: '22:00' }

/** Full-day range for users who plan in the evening/night. */
export const VOLLEDIGE_DAG_RANGE: AgendaTimeRange = { startTime: '00:00', endTime: '24:00' }

/**
 * The answer to "who are this organization's behandelaars?", with the one distinction a
 * plain array cannot carry: an empty pool because nobody is designated, and an empty pool
 * because the read was refused.
 *
 * They need opposite words. "Vraag een beheerder om behandelaars aan te wijzen" is a
 * designation gap, and saying it over a refusal sends a practice to fix something that is not
 * broken — which is what it did for 333 refused reads in a week (#4156, #3752).
 */
export interface BehandelaarRoster {
  /** The organization's active, designated behandelaars. Empty when [refused]. */
  behandelaars: BehandelaarSummary[]
  /** True when the API refused or failed the read, so the empty list means nothing. */
  refused: boolean
}

/**
 * Sentinel `userId` selecting the authenticated user's own resources — routed
 * to the `/users/me/...` endpoints instead of `/users/{userId}/...`. Lets the
 * WorkingHoursEditor stay a single component for both self-service and admin
 * (T7a/T7b, #2922).
 */
export const WORKING_HOURS_SELF = 'me'

/**
 * User Profile Store
 *
 * Manages the current user's profile data and provides actions
 * for fetching and updating profile information.
 */
export const useUserProfileStore = defineStore('userProfile', () => {
  const t = getGlobalT()

  // State
  const profile = ref<UserProfile | null>(null)
  /**
   * What the API authorises this session to do in the active organization — read from the API
   * rather than the Firebase claim, so it is the very set every `@PreAuthorize` answers from.
   * Fetched with the profile because both describe the user in the active organization.
   */
  const permissions = ref<PermissionValue[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  let inFlightFetch: Promise<void> | null = null

  // Computed
  const hasProfile = computed(() => profile.value !== null)

  /**
   * The user's chosen agenda time range, defaulting to Werkdag (07:00–22:00)
   * when no valid preference is stored. Reads `preferences.agenda` (#2893).
   */
  const agendaTimeRange = computed<AgendaTimeRange>(() => {
    const agenda = profile.value?.preferences?.agenda
    if (agenda && typeof agenda.startTime === 'string' && typeof agenda.endTime === 'string') {
      return { startTime: agenda.startTime, endTime: agenda.endTime }
    }
    return WERKDAG_RANGE
  })

  const fullName = computed(() => (profile.value ? getFullName(profile.value) : ''))

  /**
   * Hold the loaded profile, and mirror it into Sentry's scope.
   *
   * The Sentry context is bound to the profile's lifetime rather than to the sign-in
   * path, so it is set wherever the profile arrives (login, invitation replay) and
   * follows a rename or e-mail change made in the settings screen (#3598).
   */
  function setProfile(loaded: UserProfile) {
    profile.value = loaded
    setSentryUserContext(loaded)
  }

  /**
   * Fetch current user's profile with retry logic for 401 errors.
   *
   * During login, there can be a race condition where Firebase authentication
   * completes but the backend hasn't fully processed the user yet (processSignIn),
   * or Firebase token propagation has latency. This causes transient 401 errors.
   *
   * This function implements exponential backoff retry specifically for 401 errors
   * during the initial profile fetch to handle this timing sensitivity.
   * (Fixes SCRIVIO-FE-3D)
   *
   * Concurrent callers share one fetch: the sign-in listener and the route guard both ask
   * for it while a fresh session navigates.
   */
  function fetchProfile(): Promise<void> {
    inFlightFetch ??= loadProfile().finally(() => {
      inFlightFetch = null
    })
    return inFlightFetch
  }

  /**
   * Resolves once the profile and permissions of this session are known, fetching them if
   * nothing has yet. Email sign-in hands control back to the login page before the sign-in
   * listener's fetch has finished, so a guard reading [permissions] without this would see
   * none and turn a deep link into the dashboard.
   */
  async function ensureLoaded(): Promise<void> {
    if (!hasProfile.value) await fetchProfile()
  }

  async function loadProfile() {
    loading.value = true
    error.value = null

    const maxRetries = 3
    const baseDelayMs = 200

    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        // Force token refresh before each attempt to ensure fresh credentials
        // This is especially important during auth state transitions
        await refreshIdToken()

        const api = initApiService()
        const [response, granted] = await Promise.all([
          api.users.getCurrentUser(),
          api.users.getMyPermissions(),
        ])
        setProfile(response.data)
        permissions.value = granted.data.permissions as PermissionValue[]
        loading.value = false
        return // Success - exit the retry loop
      } catch (err: unknown) {
        // The refresh token is gone (expired, revoked, or the account was
        // disabled), so the force-refresh above can never succeed. Sign the user
        // out instead of surfacing a failed profile load — and stay silent, since
        // logging it is what turned an expected end-of-session into a recurring
        // Sentry error (Fixes SCRIVIO-FE-8T).
        if (isSessionExpiredError(err)) {
          await handleSessionExpired()
          loading.value = false
          return
        }

        const is401 = isAxiosError(err) && err.response?.status === 401
        const hasMoreRetries = attempt < maxRetries

        if (is401 && hasMoreRetries) {
          // Exponential backoff: 200ms, 400ms, 800ms
          const delayMs = baseDelayMs * Math.pow(2, attempt)
          console.warn(
            `Profile fetch returned 401, retrying in ${delayMs}ms (attempt ${attempt + 1}/${maxRetries + 1})`
          )
          await new Promise(resolve => setTimeout(resolve, delayMs))
          continue
        }

        // Final attempt failed or non-401 error - give up
        error.value = extractApiErrorMessage(err, t('errors.loadProfile'))
        logUnexpectedFailure('Failed to fetch profile:', err)
        loading.value = false
        throw err
      }
    }
  }

  /**
   * Update current user's profile
   */
  async function updateProfile(updates: UpdateUserProfileRequest) {
    loading.value = true
    error.value = null

    try {
      const api = initApiService()
      const response = await api.users.updateCurrentUser(updates)
      setProfile(response.data)
      return response.data
    } catch (err: unknown) {
      error.value = extractApiErrorMessage(err, t('errors.updateProfile'))
      logUnexpectedFailure('Failed to update profile:', err)
      throw err
    } finally {
      loading.value = false
    }
  }

  /**
   * Persist the agenda time range under `preferences.agenda`.
   *
   * Merges into the existing preferences object so other keys (e.g. theme)
   * are preserved — the backend replaces the whole `preferences` JSON on
   * update, so the merge must happen here (#2893).
   */
  async function updateAgendaTimeRange(range: AgendaTimeRange) {
    const currentPreferences = profile.value?.preferences ?? {}
    await updateProfile({
      preferences: { ...currentPreferences, agenda: { ...range } }
    })
  }

  /**
   * Fetch the organization's active, designated behandelaars.
   *
   * Reads the purpose-scoped roster endpoint rather than the user-administration list
   * (#4156). The list endpoint requires `users.read`, which the default Behandelaar role does
   * not hold, so every behandelaar got a 403 that nothing surfaced: `logUnexpectedFailure`
   * returns before logging on a client error, and the caller could not tell the resulting `[]`
   * from "this organization has designated nobody".
   *
   * The refusal is therefore reported rather than flattened. It should now be unreachable —
   * the roster reads on `clients.read`, without which no client dossier opens at all — but a
   * caller that cannot tell the two apart is exactly what made the previous defect silent, and
   * a permission narrowing is not the only way a read fails.
   */
  async function fetchBehandelaarRoster(): Promise<BehandelaarRoster> {
    try {
      const api = initApiService()
      const response = await api.users.listBehandelaars()
      return { behandelaars: sortByFullName(response.data ?? []), refused: false }
    } catch (err: unknown) {
      logUnexpectedFailure('Failed to fetch behandelaar roster:', err)
      return { behandelaars: [], refused: true }
    }
  }

  // ── Working hours & availability exceptions (#2922) ─────────────────────────
  //
  // Thin API proxies keyed by `userId` ('me' → self endpoints, a UUID → the
  // user-management endpoints). No state is held here on purpose: the editor
  // component owns its editing state so the same store works for any number of
  // users (self-service profile + admin) without collisions.

  /** Fetch a user's weekly working-hours pattern (always all seven weekdays). */
  async function fetchWorkingHours(userId: string): Promise<WorkingHours> {
    const api = initApiService()
    const response = userId === WORKING_HOURS_SELF
      ? await api.workingHours.getMyWorkingHours()
      : await api.workingHours.getUserWorkingHours(userId)
    return response.data
  }

  /** Replace a user's full weekly working-hours pattern. */
  async function saveWorkingHours(
    userId: string,
    request: UpdateWorkingHoursRequest
  ): Promise<WorkingHours> {
    const api = initApiService()
    const response = userId === WORKING_HOURS_SELF
      ? await api.workingHours.updateMyWorkingHours(request)
      : await api.workingHours.updateUserWorkingHours(userId, request)
    return response.data
  }

  /** List a user's availability exceptions (holidays, absences, adjusted hours). */
  async function fetchAvailabilityExceptions(userId: string): Promise<AvailabilityException[]> {
    const api = initApiService()
    const response = userId === WORKING_HOURS_SELF
      ? await api.workingHours.listMyAvailabilityExceptions()
      : await api.workingHours.listUserAvailabilityExceptions(userId)
    return response.data.exceptions
  }

  /** Create a new availability exception for a user. */
  async function createAvailabilityException(
    userId: string,
    request: AvailabilityExceptionRequest
  ): Promise<AvailabilityException> {
    const api = initApiService()
    const response = userId === WORKING_HOURS_SELF
      ? await api.workingHours.createMyAvailabilityException(request)
      : await api.workingHours.createUserAvailabilityException(userId, request)
    return response.data
  }

  /** Update an existing availability exception for a user. */
  async function updateAvailabilityException(
    userId: string,
    exceptionId: string,
    request: AvailabilityExceptionRequest
  ): Promise<AvailabilityException> {
    const api = initApiService()
    const response = userId === WORKING_HOURS_SELF
      ? await api.workingHours.updateMyAvailabilityException(exceptionId, request)
      : await api.workingHours.updateUserAvailabilityException(userId, exceptionId, request)
    return response.data
  }

  /** Delete an availability exception for a user. */
  async function deleteAvailabilityException(userId: string, exceptionId: string): Promise<void> {
    const api = initApiService()
    if (userId === WORKING_HOURS_SELF) {
      await api.workingHours.deleteMyAvailabilityException(exceptionId)
    } else {
      await api.workingHours.deleteUserAvailabilityException(userId, exceptionId)
    }
  }

  /**
   * Clear profile data (on logout)
   */
  function clearProfile() {
    profile.value = null
    permissions.value = []
    error.value = null
    loading.value = false
    // A shared machine must not attribute the next visitor's events to this account.
    clearSentryUserContext()
  }

  return {
    // State
    profile,
    permissions,
    loading,
    error,

    // Computed
    hasProfile,
    fullName,
    agendaTimeRange,

    // Actions
    fetchProfile,
    ensureLoaded,
    updateProfile,
    updateAgendaTimeRange,
    fetchBehandelaarRoster,
    fetchWorkingHours,
    saveWorkingHours,
    fetchAvailabilityExceptions,
    createAvailabilityException,
    updateAvailabilityException,
    deleteAvailabilityException,
    clearProfile
  }
})
